/**
 * InquiryStatusPage - Public status tracking page
 * No authentication required - accessed via unique token
 */

import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  getInquiryStatus,
  formatInquirySource,
  formatInquiryStatus,
  getStatusColor,
  getSlaColor,
  type InquiryStatusResponse,
} from '../../api/inquiries';
import { getFriendlyErrorMessage } from '../../api/client-error';

export default function InquiryStatusPage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, user, logout } = useAuth();
  const [inquiry, setInquiry] = useState<InquiryStatusResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!token) {
      setError('Invalid tracking link');
      setLoading(false);
      return;
    }

    getInquiryStatus(token)
      .then(setInquiry)
      .catch((err) => setError(getFriendlyErrorMessage(err, 'Inquiry not found')))
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-warm-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto mb-4" />
          <p className="text-warm-600">Loading inquiry status...</p>
        </div>
      </div>
    );
  }

  if (error || !inquiry) {
    return (
      <div className="min-h-screen bg-warm-50">
        <Header isAuthenticated={isAuthenticated} user={user} logout={logout} />
        <main className="max-w-2xl mx-auto px-4 py-16">
          <div className="card text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-danger-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-danger-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-warm-900 mb-2">Inquiry Not Found</h2>
            <p className="text-warm-600 mb-6">
              {error || 'We couldn\'t find an inquiry with this tracking link.'}
            </p>
            <Link to="/" className="btn-primary">
              Return Home
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const statusSteps = [
    { key: 'pending', label: 'Submitted' },
    { key: 'in_review', label: 'In Review' },
    { key: 'responded', label: 'Responded' },
  ];

  const currentStepIndex = statusSteps.findIndex((step) => step.key === inquiry.status);
  const isConverted = inquiry.status === 'converted';
  const isClosed = inquiry.status === 'closed';

  return (
    <div className="min-h-screen bg-warm-50">
      <Header isAuthenticated={isAuthenticated} user={user} logout={logout} />

      <main className="max-w-2xl mx-auto px-4 py-8">
        {/* Status Header */}
        <div className="card mb-6">
          <div className="flex items-center justify-between mb-4">
            <h1 className="text-xl font-display font-bold text-warm-900">Inquiry Status</h1>
            <span className={`px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(inquiry.status)}`}>
              {formatInquiryStatus(inquiry.status)}
            </span>
          </div>

          <div className="text-sm text-warm-600 space-y-1">
            <p><span className="font-medium">Class:</span> {inquiry.showName}</p>
            <p><span className="font-medium">Type:</span> {formatInquirySource(inquiry.source)}</p>
            <p><span className="font-medium">Submitted:</span> {new Date(inquiry.createdAt).toLocaleDateString('en-US', {
              month: 'long',
              day: 'numeric',
              year: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}</p>
          </div>
        </div>

        {/* Progress Steps */}
        {!isConverted && !isClosed && (
          <div className="card mb-6">
            <h2 className="font-semibold text-warm-900 mb-4">Progress</h2>
            <div className="relative">
              {/* Progress Line */}
              <div className="absolute top-4 left-4 right-4 h-0.5 bg-warm-200">
                <div
                  className="absolute top-0 left-0 h-full bg-primary-500 transition-all duration-500"
                  style={{ width: `${(currentStepIndex / (statusSteps.length - 1)) * 100}%` }}
                />
              </div>

              {/* Steps */}
              <div className="relative flex justify-between">
                {statusSteps.map((step, index) => {
                  const isComplete = index <= currentStepIndex;
                  const isCurrent = index === currentStepIndex;

                  return (
                    <div key={step.key} className="flex flex-col items-center">
                      <div
                        className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium z-10 ${
                          isComplete
                            ? 'bg-primary-500 text-white'
                            : 'bg-warm-200 text-warm-500'
                        } ${isCurrent ? 'ring-4 ring-primary-100' : ''}`}
                      >
                        {isComplete ? (
                          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        ) : (
                          index + 1
                        )}
                      </div>
                      <span className={`mt-2 text-xs font-medium ${isCurrent ? 'text-primary-600' : 'text-warm-500'}`}>
                        {step.label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Estimated Response Time */}
            <div className="mt-6 pt-4 border-t border-warm-200">
              <div className="flex items-center gap-2">
                <svg className={`w-4 h-4 ${getSlaColor(inquiry.slaStatus)}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-sm text-warm-600">
                  {inquiry.slaStatus === 'on_track'
                    ? `Expected response by ${new Date(inquiry.estimatedResponseTime).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })}`
                    : inquiry.slaStatus === 'warning'
                    ? 'Response coming soon'
                    : 'We apologize for the delay - your inquiry is being prioritized'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Converted Status */}
        {isConverted && (
          <div className="card mb-6 bg-success-50 border-success-200">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-success-100 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-success-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-success-900 mb-1">Converted to Booking!</h2>
                <p className="text-success-700 text-sm">
                  Your inquiry has been converted to a booking. Check your email for confirmation details.
                </p>
                {inquiry.bookingLink && (
                  <Link to={inquiry.bookingLink} className="btn-primary mt-4 inline-block">
                    View Booking
                  </Link>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Closed Status */}
        {isClosed && (
          <div className="card mb-6 bg-warm-100">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-warm-200 flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-warm-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div>
                <h2 className="font-semibold text-warm-900 mb-1">Inquiry Closed</h2>
                <p className="text-warm-600 text-sm">
                  This inquiry has been closed. If you have further questions, please submit a new inquiry.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Conversation Thread */}
        {(inquiry.message || inquiry.responseMessage) && (
          <div className="card mb-6">
            <h2 className="font-semibold text-warm-900 mb-4">Conversation</h2>
            <div className="space-y-4">
              {/* Studio's original message */}
              {inquiry.message && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-warm-200 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-warm-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm font-medium text-warm-900">You</span>
                      <span className="text-xs text-warm-400">
                        {new Date(inquiry.createdAt).toLocaleDateString('en-US', {
                          month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                        })}
                      </span>
                    </div>
                    <div className="bg-warm-50 rounded-xl rounded-tl-sm px-4 py-3">
                      <div className="text-sm text-warm-700 whitespace-pre-line">{inquiry.message}</div>
                    </div>
                  </div>
                </div>
              )}

              {/* SDA response */}
              {inquiry.responseMessage && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <svg className="w-4 h-4 text-primary-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 mb-1">
                      <span className="text-sm font-medium text-warm-900">Stage Door Access</span>
                      {inquiry.respondedAt && (
                        <span className="text-xs text-warm-400">
                          {new Date(inquiry.respondedAt).toLocaleDateString('en-US', {
                            month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
                          })}
                        </span>
                      )}
                    </div>
                    <div className="bg-primary-50 rounded-xl rounded-tl-sm px-4 py-3">
                      <div
                        className="text-sm text-warm-700 prose prose-sm max-w-none"
                        dangerouslySetInnerHTML={{ __html: inquiry.responseMessage }}
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="card bg-warm-50">
          <h3 className="font-medium text-warm-900 mb-3">Need Help?</h3>
          <p className="text-sm text-warm-600 mb-4">
            If you have questions about your inquiry, please contact us at{' '}
            <a href="mailto:support@stagedooraccess.com" className="text-primary-600 hover:text-primary-700">
              support@stagedooraccess.com
            </a>
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link to="/events" className="btn-primary flex-1 text-center">
              Browse Classes
            </Link>
            {isAuthenticated ? (
              <Link to="/dashboard/inquiries" className="btn-secondary flex-1 text-center">
                My Inquiries
              </Link>
            ) : (
              <Link to="/login" className="btn-secondary flex-1 text-center">
                Sign In
              </Link>
            )}
          </div>
        </div>

        {/* Tracking Link */}
        <p className="text-center text-xs text-warm-500 mt-6">
          Bookmark this page to check your status anytime.
        </p>
      </main>
    </div>
  );
}

/**
 * Header component - auth-aware
 */
function Header({ isAuthenticated, user, logout }: {
  isAuthenticated: boolean;
  user: { email?: string; role?: string } | null;
  logout: () => Promise<void>;
}) {
  const getDashboardUrl = () => {
    if (!user) return '/dashboard';
    switch (user.role) {
      case 'admin':
      case 'super_admin':
        return '/admin';
      case 'faculty':
        return '/faculty';
      default:
        return '/dashboard';
    }
  };

  return (
    <header className="bg-white border-b border-warm-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-2">
            <img src="/logo.png" alt="Stage Door Access" className="h-7" />
          </Link>

          <div className="flex items-center gap-4">
            {isAuthenticated ? (
              <>
                <span className="text-warm-600 text-sm hidden sm:block">{user?.email}</span>
                <Link to={getDashboardUrl()} className="text-warm-600 hover:text-warm-900 text-sm">
                  Dashboard
                </Link>
                <button
                  onClick={() => logout()}
                  className="text-warm-600 hover:text-warm-900 text-sm font-medium"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="text-warm-600 hover:text-warm-900">Sign In</Link>
                <Link to="/register" className="btn-primary">Get Started</Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
